import { createFileRoute } from '@tanstack/react-router'
import { useRef, useState } from 'react'
import Upload from '~icons/material-symbols/upload'
import { css } from '../../../../../styled-system/css'
import { LoadingOverlay } from '../../../../components/loading-overlay'
import { useUploadHokkaidoHouseholderCSV } from '../../../../libs/admin/hooks'
import { button } from '../../../../styles/buttonStyles'
import { contentWrapper } from '../../../../styles/contentWrapper'
import { pageTitle, sectionTitle, sectionWrapper, titleWrapper } from '../../../../styles/sectionStyles'

export const Route = createFileRoute('/admin/hokkaido/csvs/')({
  component: RouteComponent,
})

const MAX_SIZE_BYTES = 500 * 1024 * 1024

type RowStatus =
  | { kind: 'pending' }
  | { kind: 'uploading' }
  | { kind: 'success' }
  | { kind: 'error'; message: string }

type Row = {
  file: File
  status: RowStatus
}

function validateFile(file: File): string | null {
  if (!file.name.toLowerCase().endsWith('.csv')) {
    return 'CSVファイルではありません'
  }
  if (file.size > MAX_SIZE_BYTES) {
    return 'ファイルサイズが大きすぎます (500MB上限)'
  }
  return null
}

function RouteComponent() {
  const upload = useUploadHokkaidoHouseholderCSV()
  const [rows, setRows] = useState<Row[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? [])
    setRows(
      files.map((file) => {
        const error = validateFile(file)
        return {
          file,
          status: error ? { kind: 'error', message: error } : { kind: 'pending' },
        }
      }),
    )
    event.target.value = ''
  }

  const updateRow = (index: number, status: RowStatus) => {
    setRows((prev) => {
      const next = prev.slice()
      const target = next[index]
      if (!target) {
        return prev
      }
      next[index] = { ...target, status }
      return next
    })
  }

  const handleUpload = async () => {
    setIsUploading(true)
    try {
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i]
        if (!row || row.status.kind !== 'pending') {
          continue
        }
        updateRow(i, { kind: 'uploading' })
        try {
          const buffer = await row.file.arrayBuffer()
          await upload.mutateAsync({
            filename: row.file.name,
            content: new Uint8Array(buffer),
          })
          updateRow(i, { kind: 'success' })
        } catch (error) {
          console.error('Failed to upload Hokkaido householder CSV:', error)
          const message = error instanceof Error ? error.message : 'アップロードに失敗しました'
          updateRow(i, { kind: 'error', message })
        }
      }
    } finally {
      setIsUploading(false)
    }
  }

  const pendingCount = rows.filter((row) => row.status.kind === 'pending').length
  const disableUpload = pendingCount === 0 || isUploading

  return (
    <div className={contentWrapper()}>
      <div className={titleWrapper()}>
        <h1 className={pageTitle()}>世帯主CSVアップロード</h1>
      </div>
      <div className={css({ display: 'flex', flexDirection: 'column', gap: '24px' })}>
        <div className={sectionWrapper()}>
          <div className={sectionTitle()}>CSVファイルを選択</div>
          <div className={css({ display: 'flex', flexDirection: 'column', gap: '12px' })}>
            <p className={css({ fontSize: '14px', color: 'text.secondary' })}>
              拡張子は .csv、1ファイルあたり 500MB 以下にしてください。複数ファイルを同時に選択できます。
              アップロード先のキーは日本時間の日付とファイル名（YYYYMMDD/&lt;ファイル名&gt;）で決まるため、同日中に同名ファイルを再アップロードした場合は最後のファイルで上書きされます。
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              multiple
              onChange={handleFileChange}
              disabled={isUploading}
              className={css({
                position: 'absolute',
                width: '1px',
                height: '1px',
                padding: 0,
                margin: '-1px',
                overflow: 'hidden',
                clip: 'rect(0,0,0,0)',
                whiteSpace: 'nowrap',
                border: 0,
              })}
            />
            <button
              type="button"
              className={button({ color: 'secondary' })}
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
            >
              <Upload width="20" height="20" />
              ファイルを選択
            </button>
          </div>
        </div>
        {rows.length > 0 && (
          <div className={sectionWrapper()}>
            <div className={sectionTitle()}>選択ファイル ({rows.length})</div>
            <div className={css({ display: 'flex', flexDirection: 'column', gap: '8px' })}>
              {rows.map((row, index) => (
                <RowItem key={`${row.file.name}-${index}`} row={row} />
              ))}
            </div>
          </div>
        )}
        <div className={css({ display: 'flex', gap: '12px', alignItems: 'center' })}>
          <button
            type="button"
            className={button({ color: 'primary' })}
            onClick={handleUpload}
            disabled={disableUpload}
          >
            アップロード {pendingCount > 0 ? `(${pendingCount}件)` : ''}
            {isUploading && <LoadingOverlay size="sm" />}
          </button>
        </div>
      </div>
    </div>
  )
}

function RowItem({ row }: { row: Row }) {
  return (
    <div
      className={css({
        display: 'flex',
        gap: '12px',
        alignItems: 'center',
        fontSize: '14px',
      })}
    >
      <span className={css({ flex: 1, color: 'text.primary' })}>
        {row.file.name} ({(row.file.size / 1024).toFixed(1)} KB)
      </span>
      <RowStatusLabel status={row.status} />
    </div>
  )
}

function RowStatusLabel({ status }: { status: RowStatus }) {
  switch (status.kind) {
    case 'pending':
      return <span className={css({ color: 'text.secondary' })}>待機中</span>
    case 'uploading':
      return <span className={css({ color: 'text.secondary' })}>アップロード中…</span>
    case 'success':
      return <span className={css({ color: 'text.primary' })}>完了</span>
    case 'error':
      return (
        <span className={css({ color: 'text.accentError' })}>{status.message}</span>
      )
  }
}
